import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import * as Sentry from '@sentry/react';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Global axios request interceptor: synchronously attach Bearer token from
// localStorage on every outgoing request. This eliminates the 401-on-first-load
// race where individual components fire axios calls before AuthContext hydrates.
// Idempotent: guarded against double-install on hot reload.
if (typeof window !== 'undefined' && !window.__realpropAxiosAuthInterceptor) {
  axios.interceptors.request.use((config) => {
    try {
      const stored = window.localStorage.getItem('token');
      if (stored && !config.headers?.Authorization) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${stored}`;
      }
    } catch (_) {
      // localStorage may be unavailable (private mode, SSR) — fall through.
    }
    return config;
  });
  window.__realpropAxiosAuthInterceptor = true;
}

// Attach non-PII user context to all subsequent Sentry events.
const applySentryUserContext = (userData) => {
  if (!userData) {
    Sentry.setUser(null);
    Sentry.setTag('plan', undefined);
    return;
  }
  Sentry.setUser({
    id: userData.id || userData.user_id,
    // Intentionally NO email / NO name / NO PII.
  });
  Sentry.setTag('plan', userData.plan_type || userData.plan || 'free');
  Sentry.setTag('role', userData.role || 'user');
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUser();
  }, []);

  const handleAuthResponse = (userData) => {
    if (userData?.organization) {
      setOrganization(userData.organization);
    }
    setUser(userData);
    applySentryUserContext(userData);
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      handleAuthResponse(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setUser(null);
      setOrganization(null);
      setToken(null);
      localStorage.removeItem('token');
      applySentryUserContext(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    handleAuthResponse(userData);
    return userData;
  };

  const signup = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/signup`, { name, email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    handleAuthResponse(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setOrganization(null);
    applySentryUserContext(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, organization, login, signup, logout, loading, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};